import '../styles/global.css';

import { datadogRum } from '@datadog/browser-rum';
import { Flowbite } from 'flowbite-react';
import type { NextPage } from 'next';
import type { AppProps } from 'next/app';
import { Inter } from 'next/font/google';
import { type ReactElement, type ReactNode, useEffect } from 'react';

import { GlobalContextProvider } from '@/contexts/GlobalContext';
import { SupabaseProvider } from '@/contexts/SupabaseContext';
import { ToastContextProvider } from '@/contexts/ToastContext';
import useAmplitudeInit from '@/hooks/useAmplitudeInit';
import { customFlowbiteTheme } from '@/utils/customFlowbiteTheme';
// If loading a variable font, you don't need to specify the font weight
const inter = Inter({ subsets: ['latin'] });

// https://nextjs.org/docs/basic-features/layouts
export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

const MyApp = ({ Component, pageProps }: AppPropsWithLayout) => {
  useAmplitudeInit();

  // supabase.auth.onAuthStateChange((event, session) => {
  //   if (event === 'SIGNED_IN' && amplitude) {
  //     amplitude.setUserId(session?.user?.id);
  //   }
  // });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      datadogRum.init({
        applicationId: '7f2f1699-b45b-4b35-b62a-ce7955f6f343',
        clientToken: 'pub588b4ccc3b09506bb52d367eeabf14fe',
        site: 'us3.datadoghq.com',
        service: 'lebra',
        env: 'prod',
        sessionSampleRate: 100,
        sessionReplaySampleRate: 20,
        defaultPrivacyLevel: 'mask-user-input',
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
      });

      datadogRum.startSessionReplayRecording();
    }
  }, []);

  const getLayout = Component.getLayout ?? ((page) => page);
  return (
    <SupabaseProvider initialSession={pageProps.initialSession}>
      <Flowbite theme={{ theme: customFlowbiteTheme }}>
        <GlobalContextProvider>
          <ToastContextProvider>
            {getLayout(
              <Component {...pageProps} className={inter.className} />
            )}
          </ToastContextProvider>
        </GlobalContextProvider>
      </Flowbite>
    </SupabaseProvider>
  );
};

export default MyApp;
